/**
 * EntityRenewalsMatrix.tsx — Renewal status matrix with child-row support.
 *
 * DLD §3.3 / actionable-additional-filings:
 *   - Rows with parentObligationId are child rows (secondary filings).
 *   - Child rows render indented, labelled with row.name, with their own
 *     next-due date, "Mark renewed" dialog, and a "Pending entry" inline
 *     date input for any dateless non-confirmed row (parent or child).
 *   - Parent rows render as before, with the standard 4-category labels.
 *   - All interactive controls have info tooltips.
 *
 * 3-state rendering (date | blank | not_required) via toDisplay().
 * Distinct matrix statuses: real | computed | awaiting_data | not_applicable
 * (mapped via mapMatrixStatus — NOT conflated with ObligationEditor statuses).
 */
import { useState, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { RefreshCw, X } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { format as formatDate } from "date-fns";
import { fetchRenewalStatus, markObligationRenewed } from "../lib/dataFetchers";
import type { ObligationKey, ObligationStatus, RenewalStatusRow } from "../lib/types";
import { toDisplay, type ObligationDisplayInput } from "../lib/obligation-display";
import { SectionCard } from "./ui/SectionCard";
import { Tooltip } from "./ui/Tooltip";
import { Button } from "./ui/Button";
import { Info } from "lucide-react";
import { cn } from "../lib/cn";
import { extractApiError } from "../lib/api";

// ─── Hooks for translated labels ─────────────────────────────────────────────

function useObligationLabels(): Record<ObligationKey, string> {
  const { t } = useTranslation("status");
  return {
    license:      t("obligation_license"),
    company:      t("obligation_company"),
    annualFiling: t("obligation_annual_filing"),
    eor:          t("obligation_eor"),
  };
}

/** Keys invalidated after any obligation mutation. */
function invalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-renewal-status", entityId] },
    { queryKey: ["entity-obligations", entityId] },
    { queryKey: ["entities"] },
    { queryKey: ["renewals"] },
    { queryKey: ["changelog-recent"] },
    { queryKey: ["entity", entityId] },
  ];
}

// ─── Status mapping ───────────────────────────────────────────────────────────

/**
 * Map matrix status → obligation display status.
 *
 * The param is widened to `ObligationStatus | string` because the backend can
 * return extended values not yet in the frontend union (e.g. "computed",
 * "estimated", "under_review", "needs_confirmation", "not_required",
 * "indefinite") during migration windows. The `as string` cast at the call site
 * is therefore no longer needed.
 */
function mapMatrixStatus(status: ObligationStatus | string): string {
  if (status === "confirmed") return "confirmed";
  if (status === "not_required" || status === "indefinite" || status === "not_applicable") return "not_required";
  return "needs_confirmation";
}

// ─── Native date input ────────────────────────────────────────────────────────

interface NativeDateInputProps {
  id?: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  label: string;
  disabled?: boolean;
}

function NativeDateInput({ id, value, onChange, label, disabled }: NativeDateInputProps): JSX.Element {
  return (
    <input
      id={id}
      type="date"
      aria-label={label}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className={cn(
        "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-base",
        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    />
  );
}

// ─── Mark-renewed dialog for matrix rows ─────────────────────────────────────

interface MatrixMarkRenewedDialogProps {
  entityId: string;
  obligationId: string;
  obligationName: string;
  nextDueDate: string | null;
  open: boolean;
  onClose: () => void;
}

function MatrixMarkRenewedDialog({
  entityId,
  obligationId,
  obligationName,
  nextDueDate,
  open,
  onClose,
}: MatrixMarkRenewedDialogProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const qc = useQueryClient();
  const uid = useId();
  const [completedDate, setCompletedDate] = useState<string | null>(
    formatDate(new Date(), "yyyy-MM-dd"),
  );
  const [comment, setComment] = useState("");
  const descId = `${uid}-mark-desc`;

  // BUG-9 fix: capture nextDueDate at dialog-open time so a background refetch
  // that updates the parent row does not silently change the expectedCurrentNextDue
  // we send to the server (which would cause a 409 concurrency mismatch).
  const [capturedNextDue] = useState(() => nextDueDate);

  const mutation = useMutation({
    mutationFn: () =>
      markObligationRenewed(entityId, obligationId, {
        completedDate: completedDate ?? formatDate(new Date(), "yyyy-MM-dd"),
        expectedCurrentNextDue: capturedNextDue ?? null,
        comment: comment.trim() || null,
      }),
    onSuccess: (data) => {
      toast.success(
        t("mark_renewed_success").replace("{{date}}", data.nextDueDate ?? t("no_date_shown")),
      );
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      onClose();
    },
    onError: (err: unknown) => {
      const ax = err as {
        response?: { status?: number; data?: { code?: string; detail?: { code?: string } } };
      };
      const code = ax.response?.data?.detail?.code ?? ax.response?.data?.code;
      if (code === "indefinite_cycle") {
        toast.error(t("mark_renewed_error_indefinite"));
      } else if (ax.response?.status === 409) {
        toast.error(t("mark_renewed_error_conflict"));
      } else if (code === "not_confirmed") {
        toast.error(t("mark_renewed_error_not_confirmed"));
      } else {
        toast.error(extractApiError(err) || t("mark_renewed_error"));
      }
    },
  });

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v && !mutation.isPending) onClose();
        if (!v) {
          setComment("");
          setCompletedDate(formatDate(new Date(), "yyyy-MM-dd"));
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content
          aria-describedby={descId}
          className={cn(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "w-[min(460px,90vw)] bg-surface rounded-[11px] border border-border shadow-pop p-6 focus:outline-none",
          )}
        >
          <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-1">
            {t("mark_renewed_title")}
          </Dialog.Title>
          <Dialog.Description id={descId} className="text-[13px] text-muted mb-5">
            {t("mark_renewed_desc")
              .replace("{{name}}", obligationName)
              .replace("{{cycle}}", "—")}
          </Dialog.Description>

          <div className="space-y-4">
            <div>
              <label
                htmlFor={`${uid}-completed`}
                className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2"
              >
                {t("mark_renewed_completed_label")}
              </label>
              <NativeDateInput
                id={`${uid}-completed`}
                value={completedDate}
                onChange={setCompletedDate}
                label={t("mark_renewed_completed_label")}
              />
            </div>
            <div>
              <label
                htmlFor={`${uid}-comment`}
                className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2"
              >
                {t("mark_renewed_comment_label")}
              </label>
              <input
                id={`${uid}-comment`}
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("mark_renewed_comment_placeholder")}
                className={cn(
                  "text-base h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
                  "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Dialog.Close asChild>
              <Button variant="secondary" disabled={mutation.isPending}>
                {t("cancel_button")}
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              disabled={mutation.isPending || !completedDate}
              onClick={() => mutation.mutate()}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", mutation.isPending && "animate-spin")}
                aria-hidden
              />
              {mutation.isPending ? t("renewing") : t("mark_renewed_confirm")}
            </Button>
          </div>

          <Dialog.Close
            aria-label={t("dialog_close")}
            className="absolute right-3 top-3 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-surface-2 text-dim focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20"
          >
            <X className="h-4 w-4" aria-hidden />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Pending-entry inline date for child rows ─────────────────────────────────
// REMOVED: the matrix is now pure read-only. Operators set dates exclusively
// via the Renewal Obligations card below to prevent accidental single-click
// writes. The "Set in Renewal Obligations below" hint is rendered inline above.

// ─── InfoTooltip (local) — keyboard-focusable (i) icon with tooltip ──────────
// Mirrors the same component in CycleScheduleEditor.tsx.

interface InfoTooltipProps {
  content: string;
  side?: "top" | "bottom" | "left" | "right";
}

function InfoTooltip({ content, side = "top" }: InfoTooltipProps): JSX.Element {
  return (
    <Tooltip content={content} side={side}>
      {/* button is natively focusable; padding expands tap target to ≥44px */}
      <button
        type="button"
        aria-label={content}
        className={cn(
          "inline-flex items-center justify-center",
          "w-[18px] h-[18px] min-h-[44px] p-[13px] -my-[13px]",
          "text-dim hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded",
          "cursor-default bg-transparent border-0",
        )}
      >
        <Info className="h-[13px] w-[13px] shrink-0" aria-hidden />
      </button>
    </Tooltip>
  );
}

// ─── Parent matrix row ────────────────────────────────────────────────────────

interface MatrixRowProps {
  entityId: string;
  row: RenewalStatusRow;
  isEven: boolean;
  canEdit: boolean;
}

function MatrixRow({ entityId, row, isEven, canEdit }: MatrixRowProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const obligationLabels = useObligationLabels();
  const [markRenewedOpen, setMarkRenewedOpen] = useState(false);

  const mappedStatus = mapMatrixStatus(row.status);

  const displayObl: ObligationDisplayInput = {
    status:        mappedStatus,
    nextDueDate:   row.next_due ?? null,
    explanation:   row.reason ?? null,
    referenceNote: null,
    schedule:      null,
  };

  const display = toDisplay(displayObl);

  const isChild = Boolean(row.parentObligationId);
  const rowLabel = isChild && row.name
    ? row.name
    : (obligationLabels[row.obligation] ?? row.obligation);

  return (
    <>
      <tr
        className={cn(
          "border-b border-border-2 last:border-b-0",
          isEven && "bg-surface-2/50",
        )}
      >
        {/* Obligation name — indented for child rows */}
        <td
          className={cn(
            "py-[11px] px-[15px] text-[13.5px] whitespace-nowrap",
            isChild
              ? "pl-8 border-l-2 border-accent/30 text-muted font-medium"
              : "text-text font-semibold",
          )}
        >
          {isChild && (
            <span className="text-dim mr-1.5" aria-hidden>↳</span>
          )}
          {rowLabel}
        </td>

        {/* Cycle */}
        <td className="py-[11px] px-[15px] text-[13.5px] text-muted whitespace-nowrap">
          {row.cycle_label ?? <span className="text-dim">—</span>}
        </td>

        {/* Next due — 3-state display OR pending-entry inline date */}
        <td className="py-[11px] px-[15px]">
          {/* Rows with no date + needs confirmation: read-only "Pending entry"
              indicator. Editing happens exclusively in the Renewal Obligations
              card below — keeping the matrix a pure summary surface prevents
              accidental writes from a single click. */}
          {row.obligation_id && !row.next_due && (row.status === "awaiting_data" || mapMatrixStatus(row.status) === "needs_confirmation") ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-0.5">
                <span className="text-[11.5px] text-amber-text font-semibold">
                  {t("matrix_awaiting_entry")}
                </span>
                <InfoTooltip content={t("sf_tooltip_pending_entry")} side="top" />
              </div>
              <span className="text-[12px] text-muted italic">
                {t("matrix_edit_via_obligations_below", {
                  defaultValue: "Set in Renewal Obligations below",
                })}
              </span>
            </div>
          ) : (
            <>
              {display.kind === "date" && (
                <>
                  <span className="font-mono text-[13.5px] font-semibold text-text">
                    {display.date}
                  </span>
                  {display.referenceNote && (
                    <span className="block text-[11.5px] text-muted mt-0.5 italic">
                      {display.referenceNote}
                    </span>
                  )}
                </>
              )}
              {display.kind === "blank" && (
                <>
                  <span className="text-dim">—</span>
                  <span className="block text-[11.5px] text-muted mt-0.5">
                    {display.hint}
                  </span>
                </>
              )}
              {display.kind === "not_required" && (
                <span className="text-[13.5px] text-dim italic">
                  {display.label ?? t("matrix_not_required")}
                </span>
              )}
            </>
          )}
        </td>

        {/* Status / actions */}
        <td className="py-[11px] px-[15px]">
          {row.reason && display.kind === "date" && (
            <span className="block text-[11.5px] text-muted mb-1">{row.reason}</span>
          )}

          {/* Mark renewed — shown on child rows when canEdit + status is "confirmed" (has a real date) */}
          {isChild && canEdit && row.obligation_id && display.kind === "date" && (
            <>
              <Tooltip content={t("sf_tooltip_mark_renewed")} side="top">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setMarkRenewedOpen(true)}
                  className="min-h-[44px] text-[11.5px]"
                  aria-label={`${t("mark_renewed_button")} — ${rowLabel}`}
                >
                  <RefreshCw className="h-3 w-3" aria-hidden />
                  {t("mark_renewed_button")}
                </Button>
              </Tooltip>
              <MatrixMarkRenewedDialog
                entityId={entityId}
                obligationId={row.obligation_id}
                obligationName={rowLabel}
                nextDueDate={row.next_due ?? null}
                open={markRenewedOpen}
                onClose={() => setMarkRenewedOpen(false)}
              />
            </>
          )}
        </td>
      </tr>
    </>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function MatrixSkeleton(): JSX.Element {
  return (
    <tbody>
      {Array.from({ length: 4 }, (_, i) => (
        <tr key={i} className="border-b border-border-2 last:border-b-0">
          <td className="py-[11px] px-[15px]">
            <div className="skeleton h-4 w-28" />
          </td>
          <td className="py-[11px] px-[15px]">
            <div className="skeleton h-4 w-16" />
          </td>
          <td className="py-[11px] px-[15px]">
            <div className="skeleton h-4 w-24" />
          </td>
          <td className="py-[11px] px-[15px]">
            <div className="skeleton h-4 w-40" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface EntityRenewalsMatrixProps {
  entityId: string;
  /** When true, shows Mark-renewed buttons on child rows. Defaults to false. */
  canEdit?: boolean;
}

export function EntityRenewalsMatrix({
  entityId,
  canEdit = false,
}: EntityRenewalsMatrixProps): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["entity-renewal-status", entityId],
    queryFn: () => fetchRenewalStatus(entityId),
    enabled: Boolean(entityId),
  });
  const { t } = useTranslation("status");
  const { t: tObl } = useTranslation("obligations");

  return (
    <SectionCard title={t("matrix_title")} hint={tObl("renewal_status_subtitle")} bodyClassName="p-0 overflow-x-auto">
      {isError ? (
        <p className="text-[13px] text-muted px-[15px] py-3">
          {t("matrix_error")}
        </p>
      ) : (
        <table
          className="w-full min-w-[480px] text-left"
          role="table"
          aria-label={t("matrix_title")}
        >
          <thead>
            <tr className="border-b border-border-2">
              <th
                scope="col"
                className="pb-[10px] pt-[5px] px-[15px] text-[11px] font-bold tracking-[.04em] uppercase text-dim whitespace-nowrap"
              >
                {t("matrix_col_obligation")}
              </th>
              <th
                scope="col"
                className="pb-[10px] pt-[5px] px-[15px] text-[11px] font-bold tracking-[.04em] uppercase text-dim whitespace-nowrap"
              >
                {t("matrix_col_cycle")}
              </th>
              <th
                scope="col"
                className="pb-[10px] pt-[5px] px-[15px] text-[11px] font-bold tracking-[.04em] uppercase text-dim whitespace-nowrap"
              >
                {t("matrix_col_next_due")}
              </th>
              <th
                scope="col"
                className="pb-[10px] pt-[5px] px-[15px] text-[11px] font-bold tracking-[.04em] uppercase text-dim"
              >
                {t("matrix_col_status")}
              </th>
            </tr>
          </thead>

          {isLoading || !data ? (
            <MatrixSkeleton />
          ) : (
            <tbody>
              {data.rows.map((row, idx) => (
                <MatrixRow
                  key={row.obligation_id ?? (row.parentObligationId ? `${row.parentObligationId}-${idx}` : `${row.obligation}-${idx}`)}
                  entityId={entityId}
                  row={row}
                  isEven={idx % 2 === 0}
                  canEdit={canEdit}
                />
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-[18px] px-[15px] text-[13px] text-dim text-center border border-dashed border-border rounded-[9px]"
                  >
                    {t("matrix_empty")}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      )}
    </SectionCard>
  );
}
